/* policy.ts
   Local type definitions and type guards for GraphView.
   Extracted from GraphView.tsx to isolate types and reuse in other modules.
*/

import type {
  PolicyRule,
  Condition,
  SimpleCondition,
  NestedCondition,
  CountCondition,
  NotCondition,
} from '../../../../types/policy';

/** Type for the 'if' part of a policy rule. */
export type PolicyRuleCondition = PolicyRule['if'];

/** A more flexible type for the condition being processed. */
export type ProcessableCondition = PolicyRuleCondition | Condition;

/**
 * Type guard: identifies a simple field-based condition.
 */
export function isSimpleCondition(condition: unknown): condition is SimpleCondition {
  return (
    condition !== null &&
    typeof condition === 'object' &&
    !Array.isArray(condition) &&
    (('field' in condition && typeof (condition as Record<string, unknown>).field === 'string') ||
     ('value' in condition && typeof (condition as Record<string, unknown>).value === 'string')) &&
    Object.keys(condition).some(
      (key) => key !== 'field' && key !== 'value' && key !== 'not' && key !== 'allOf' && key !== 'anyOf' && key !== 'count'
    )
  );
}

/**
 * Type guard: identifies a count-based condition.
 */
export function isCountCondition(condition: unknown): condition is CountCondition {
  return (
    condition !== null &&
    typeof condition === 'object' &&
    'count' in condition &&
    typeof (condition as Record<string, unknown>).count === 'object' &&
    (condition as Record<string, unknown>).count !== null &&
    ('field' in ((condition as Record<string, unknown>).count as Record<string, unknown>) ||
     'value' in ((condition as Record<string, unknown>).count as Record<string, unknown>)) &&
    (
      'greater' in condition ||
      'less' in condition ||
      'equals' in condition ||
      'greaterOrEquals' in condition ||
      'lessOrEquals' in condition ||
      'notEquals' in condition
    )
  );
}

/**
 * Type guard: identifies a nested group condition.
 */
export function isNestedCondition(condition: unknown): condition is NestedCondition {
  return (
    condition !== null &&
    typeof condition === 'object' &&
    ('allOf' in condition && Array.isArray((condition as Record<string, unknown>).allOf) ||
     'anyOf' in condition && Array.isArray((condition as Record<string, unknown>).anyOf))
  );
}

/**
 * Type guard: identifies a negation wrapper.
 */
export function isNotCondition(condition: unknown): condition is NotCondition {
  return (
    condition !== null &&
    typeof condition === 'object' &&
    'not' in condition &&
    typeof (condition as Record<string, unknown>).not === 'object' &&
    (condition as Record<string, unknown>).not !== null
  );
} 